import { useRef } from "react";
import { createPortal } from "react-dom";
import { RemoveScroll } from "react-remove-scroll";
import { useTranslation } from "react-i18next";
import {
  closeDesktopSettings,
  useDesktopSettings,
  type DesktopSettingsPage,
} from "../../../domains/app/stores/desktopSettingsStore";
import {
  useCurrentUser,
  type CurrentUser,
} from "../../../domains/user/hooks/useCurrentUser";
import { useModalKeyboard } from "../../../components/app/DesktopDialogOverlay";
import { SettingsCloseButton } from "./components/SettingsDialog";
import { SettingsNav, type SettingsNavEntry } from "./components/SettingsNav";
import {
  AccountSection,
  DeleteAccountSection,
  DeleteConversationsSection,
} from "./sections/AccountSection";
import { AvatarSection } from "./sections/AvatarSection";
import { CertificationSection } from "./sections/CertificationSection";
import { InterestsSection } from "./sections/InterestsSection";
import { LanguageSection } from "./sections/LanguageSection";
import { LegalSection, PartnerPrivacySection } from "./sections/LegalSection";
import { NotificationsSection } from "./sections/NotificationsSection";
import {
  PartnerSharingSection,
  PartnersSection,
} from "./sections/PartnerSharingSection";
import { PracticeSection } from "./sections/PracticeSection";
import { RoomsSection } from "./sections/RoomsSection";
import { SpecialtySection } from "./sections/SpecialtySection";

function navEntryOf(page: DesktopSettingsPage): SettingsNavEntry {
  switch (page.page) {
    case "account":
    case "avatar":
    case "deleteAccount":
    case "deleteConversations":
    case "certification":
      return "account";
    case "specialty":
      return "specialty";
    case "language":
      return "language";
    case "notifications":
      return "notifications";
    case "partnerSharing":
    case "partners":
      return "partnerSharing";
    case "practice":
    case "interests":
      return "practice";
    case "rooms":
      return "rooms";
    case "terms":
      return "terms";
    case "privacy":
    case "partnerPrivacy":
      return "privacy";
    default:
      throw new Error(page satisfies never);
  }
}

function renderPage(
  page: DesktopSettingsPage,
  currentUser: CurrentUser
): React.JSX.Element {
  switch (page.page) {
    case "account":
      return (
        <AccountSection currentUser={currentUser} editing={page.editing} />
      );
    case "avatar":
      return <AvatarSection />;
    case "deleteAccount":
      return <DeleteAccountSection currentUser={currentUser} />;
    case "deleteConversations":
      return <DeleteConversationsSection />;
    case "specialty":
      return <SpecialtySection currentUser={currentUser} />;
    case "language":
      return <LanguageSection />;
    case "notifications":
      return <NotificationsSection currentUser={currentUser} />;
    case "partnerSharing":
      return <PartnerSharingSection />;
    case "partners":
      return <PartnersSection />;
    case "practice":
      return <PracticeSection currentUser={currentUser} />;
    case "interests":
      return <InterestsSection />;
    case "rooms":
      return <RoomsSection leaving={page.leaving} />;
    case "certification":
      return <CertificationSection currentUser={currentUser} />;
    case "terms":
      return <LegalSection doc="terms" tab={page.tab} />;
    case "privacy":
      return <LegalSection doc="privacy" tab={page.tab} />;
    case "partnerPrivacy":
      return <PartnerPrivacySection organisationId={page.organisationId} />;
    default:
      throw new Error(page satisfies never);
  }
}

/**
 * The desktop settings (Figma "06 - Settings & compte"): a modal over the app,
 * opened from the account menu, ⇧⌘, or a `/settings/*` address.
 *
 * Mounted once by the desktop shell, for a signed-in reader only, and driven by
 * `desktopSettingsStore`. The reader stays on the page they were reading, which
 * is the point of a modal over a page: closing it puts them back where they
 * were.
 */
export function DesktopSettingsHost(): React.JSX.Element | null {
  const settings = useDesktopSettings();
  const { currentUser } = useCurrentUser();
  if (settings.status === "closed" || currentUser === null) return null;
  return (
    <DesktopSettingsModal page={settings.page} currentUser={currentUser} />
  );
}

function DesktopSettingsModal({
  page,
  currentUser,
}: {
  page: DesktopSettingsPage;
  currentUser: CurrentUser;
}): React.JSX.Element {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  useModalKeyboard({
    cardRef,
    onClose: closeDesktopSettings,
    enabled: true,
  });

  return createPortal(
    <RemoveScroll enabled>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("Settings")}
        className="fixed inset-0 z-999 flex items-center justify-center bg-ds-black/35 p-6 scrollbar-subtle"
        onClick={closeDesktopSettings}
      >
        <div
          ref={cardRef}
          className="relative flex h-195 max-h-full w-240 max-w-full overflow-hidden rounded-2xl bg-ds-white shadow-desktop-modal"
          // The card is inside the backdrop, so a click on it would close the
          // modal through the backdrop's handler.
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <SettingsNav active={navEntryOf(page)} />
          {renderPage(page, currentUser)}
          <SettingsCloseButton
            onClose={closeDesktopSettings}
            size="md"
            className="absolute top-4.5 right-5"
          />
        </div>
      </div>
    </RemoveScroll>,
    document.body
  );
}
